import { zodResolver } from '@hookform/resolvers/zod';
import { startOfDay } from 'date-fns';
import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useSpace } from '../../../../hooks/useSpace';
import { usePostMeeting } from '../../api/usePostMeeting';
import { useUpdateMeeting } from '../../api/useUpdateMeeting';
import { DisplayedMeeting } from '../../types';
import { DocumentMinimalData } from '../../../../types';
import { useChangeDocumentsOfResource } from '../../../documents/hooks/useChangeDocumentsOfResource';
import useUploadFilesToGoogleDrive from '../../../documents/api/useUploadFileToGoogleDrive';
import { createMeetingGoogleDoc } from '../../api/createMeetingGoogleDoc';
import { generateMeetingAgenda } from '../../api/generateMeetingAgenda';
import { enqueueSnackbar } from '../../../../components/GlobalSnackbar/snackbarStore';
import useGetDriveConfig from '../../../../features/google-drive/api/useGetDriveConfig';

const formSchema = z.object({
  title: z.string().min(1, 'Le titre est requis'),
  description: z.string(),
  start_time: z.string(),
});

export type CreateUpdateMeetingFormData = z.infer<typeof formSchema>;

export function useCreateUpdateMeetingForm(
  onClose: () => void,
  meeting?: DisplayedMeeting
) {
  const { currentSpace } = useSpace();
  const { mutateAsync: createMeeting, isPending: isCreating } = usePostMeeting();
  const { mutateAsync: updateMeeting, isPending: isUpdating } = useUpdateMeeting();
  const { uploadPendingFiles, isPending: isUploading } = useChangeDocumentsOfResource();
  const { mutateAsync: uploadFiles, isPending: isUploadingDrive } = useUploadFilesToGoogleDrive();
  const { data: driveConfig } = useGetDriveConfig();
  const isDriveConnected = !!driveConfig;
  const [serverError, setServerError] = React.useState<string | null>(null);
  const [isGeneratingAgenda, setIsGeneratingAgenda] = React.useState(false);

  const isPending = isCreating || isUpdating || isUploading || isUploadingDrive;

  // The meeting report: an uploaded/linked document, resolved to a URL on submit.
  const [document, setDocument] = React.useState<DocumentMinimalData | null>(
    meeting?.report_url
      ? {
          id: meeting.report_document_id ?? crypto.randomUUID(),
          name: meeting.title,
          file_url: meeting.report_url,
          type: 'Réunion CS',
        }
      : null
  );

  const today = React.useMemo(() => {
    return startOfDay(new Date()).toISOString();
  }, []);

  const form = useForm<CreateUpdateMeetingFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: meeting?.title ?? 'Réunion CS',
      description: meeting?.description ?? '',
      start_time: meeting?.start_time ?? today,
    },
  });

  const handleGenerateAgenda = async () => {
    if (!currentSpace?.id) return;

    const currentDescription = form.getValues('description');
    if (
      currentDescription &&
      currentDescription.trim() &&
      currentDescription !== ''
    ) {
      const confirmReplace = window.confirm(
        "Remplacer le contenu existant par l'ordre du jour généré ?"
      );
      if (!confirmReplace) return;
    }

    try {
      setIsGeneratingAgenda(true);
      const agendaHtml = await generateMeetingAgenda({
        spaceId: currentSpace.id,
        title: form.getValues('title'),
        startTime: form.getValues('start_time'),
      });
      form.setValue('description', agendaHtml, { shouldDirty: true, shouldValidate: true });
      enqueueSnackbar("Ordre du jour généré avec succès.", "success");
    } catch (err) {
      console.error("Erreur lors de la génération de l'ordre du jour:", err);
      enqueueSnackbar("Erreur lors de la génération de l'ordre du jour.", "error");
    } finally {
      setIsGeneratingAgenda(false);
    }
  };

  // Ignore partial or invalid entries while the date is being typed.
  function handleStartTimeChange(date: Date | null) {
    if (date && !Number.isNaN(date.getTime())) {
      form.setValue('start_time', date.toISOString(), { shouldDirty: true });
    }
  }

  const onSubmit = async (data: CreateUpdateMeetingFormData) => {
    if (!currentSpace?.id) return;

    setServerError(null);

    try {
      let reportUrl: string | null = null;

      if (meeting) {
        // Push any pending file to Drive, then persist its resolved URL.
        const [resolved] = document
          ? await uploadPendingFiles([{ ...document, name: data.title }], { folderName: 'Réunions' })
          : [];
        reportUrl = resolved?.file_url ?? null;

        await updateMeeting({
          id: meeting.id,
          meeting: {
            title: data.title,
            description: data.description,
            start_time: data.start_time,
          },
          reportUrl,
          reportDocumentId: meeting.report_document_id ?? null,
        });
      } else {
        if (isDriveConnected) {
          reportUrl = await createMeetingGoogleDoc({
            spaceId: currentSpace.id,
            title: data.title,
            content: data.description,
            startTime: data.start_time,
            uploadFiles,
          });
        }

        await createMeeting({
          command: {
            title: data.title,
            description: data.description,
            start_time: data.start_time,
          },
          reportUrl,
        });
      }
      onClose();
    } catch (err) {
      console.error('Erreur lors de l’enregistrement de la réunion:', err);
      setServerError(
        err instanceof Error ? err.message : "L'enregistrement de la réunion a échoué. Réessayez."
      );
    }
  };

  return {
    form,
    document,
    setDocument,
    onSubmit: form.handleSubmit(onSubmit),
    isPending,
    serverError,
    handleStartTimeChange,
    handleGenerateAgenda,
    isGeneratingAgenda,
    isDriveConnected,
  };
}
